import React, { useEffect, useState } from "react";
import { X, Mail, Phone, Briefcase, MapPin, Calendar, Cake, User2, Building2, Network, History, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const contractLabels = {
  CLT_FrankCrum: "CLT · FrankCrum",
  PJ_Lummar: "PJ · Lummar",
  PJ_Direct: "PJ · Direct",
};

const fmtMoney = (n) => n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function SalaryHistorySection({ targetUserId, viewerRole }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const canSee = ["hr", "admin", "ceo"].includes(viewerRole);

  useEffect(() => {
    if (!canSee || !open || items !== null) return;
    api
      .get(`/users/${targetUserId}/salary-history`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [open, canSee, targetUserId, items]);

  if (!canSee) return null;

  return (
    <div className="mt-4 border-t border-[#f1f5f9] pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="profile-salary-history-toggle"
        className="w-full flex items-center justify-between text-left text-[12px] text-[#475569] hover:text-[#1e293b]"
      >
        <span className="inline-flex items-center gap-2 font-semibold uppercase tracking-[0.12em]">
          <History size={13} /> {t("profile_modal.salary_history")}
        </span>
        <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 flex flex-col gap-2" data-testid="profile-salary-history">
          {items === null && <div className="text-[12px] text-[#94a3b8]">{t("profile_modal.loading")}</div>}
          {items && items.length === 0 && <div className="text-[12px] text-[#94a3b8] italic">{t("profile_modal.no_salary_movement")}</div>}
          {items && items.map((h) => (
            <div key={h.id} className="rounded-[10px] border border-[#e2e8f0] p-2.5">
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="font-semibold text-[#1e293b]">{fmtMoney(h.from_salary)} → {fmtMoney(h.to_salary)}</span>
                <span className="text-[10px] text-[#94a3b8]">{h.effective_date}</span>
              </div>
              <div className="text-[11.5px] text-[#475569] mt-0.5">{h.reason}</div>
              {h.out_of_band && <span className="inline-block mt-1 text-[10px] text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">{t("profile_modal.out_of_band")}</span>}
              {h.recorded_by && <div className="text-[10px] text-[#94a3b8] mt-1">{t("profile_modal.by")} {h.recorded_by}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#f1f5f9] last:border-0">
      <Icon size={15} className="text-[#94a3b8] shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#94a3b8]">{label}</div>
        <div className="text-[13.5px] text-[#1e293b] mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
};

export default function EmployeeProfileModal({ user, manager, department, onClose, children }) {
  const { user: me } = useAuth();
  const { t } = useI18n();
  if (!user) return null;
  const initials = user.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
  const isFormer = user.active === false;
  // Privacy: contract type visible only to HR/Admin/CEO/manager-of-user/self
  const canSeeContractType =
    me && (
      ["hr", "admin", "ceo"].includes(me.role) ||
      me.id === user.id ||
      user.manager_id === me.id
    );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="employee-profile-modal"
        className="bg-white rounded-[20px] max-w-[560px] w-full shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 pb-5 bg-gradient-to-br from-[#1e293b] via-[#475569] to-[#334155] text-white rounded-t-[20px]">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="employee-profile-close"><X size={20} /></button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-[20px] font-semibold shrink-0">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/70">{user.role}{isFormer && ` · ${t("profile_modal.former")}`}</div>
              <h3 className="text-[22px] font-semibold leading-tight mt-1 truncate">{user.name}</h3>
              <div className="text-[13px] text-white/80 mt-1 truncate">{user.job_title || "—"}</div>
              {user.contract_type && canSeeContractType && (
                <span className="inline-block mt-2 text-[10px] uppercase tracking-wider bg-white/15 backdrop-blur px-2.5 py-1 rounded-full">{contractLabels[user.contract_type] || user.contract_type}</span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 pt-4">
          <Row icon={Mail} label={t("common.work_email")} value={user.email} />
          <Row icon={Mail} label={t("common.personal_email")} value={user.personal_email} />
          <Row icon={Phone} label={t("common.phone")} value={user.phone} />
          <Row icon={Building2} label={t("common.department")} value={department?.name} />
          <Row icon={Briefcase} label={t("common.job_title")} value={user.job_title} />
          <Row icon={MapPin} label={t("common.location")} value={user.location} />
          <Row icon={Network} label={t("common.manager")} value={manager?.name} />
          <Row icon={Calendar} label={t("common.hire_date")} value={user.hire_date?.slice(0, 10)} />
          <Row icon={Cake} label={t("common.birthday")} value={user.birth_date} />
          <Row icon={User2} label={t("common.bio")} value={user.bio} />
          <SalaryHistorySection targetUserId={user.id} viewerRole={me?.role} />
          {children}
        </div>
      </div>
    </div>
  );
}
